import el from "@cypherpotato/el";
import dayjs from "dayjs";
import { copyToClipboard } from "../ui/clipboard";
import { formatNumber } from "../ui/format";
import { renderMarkdown } from "../ui/md-renderer";
import { cond } from "../util";
import { Button, ButtonRow, ToggleButton } from "./button";
import { Icon } from "./icon";
import { BlockCodeWrapper, InlineCode, MutedText } from "./text";
import { displayAlert } from "../ui/alert";

function ConversationRole(role) {
    return cond(
        [role == "user", el(".conversation-role", Icon("user-line"), "User", { style: { color: "#1AA82D" } })],
        [role == "assistant", el(".conversation-role", Icon("robot-2-line"), "Assistant", { style: { color: "#687FB6" } })],
        [role == "judge", el(".conversation-role", Icon("scales-3-line"), "Judge", { style: { color: "#B5792A" } })],
        [role == "system", el(".conversation-role", Icon("cpu-line"), "System", { style: { color: "#A26F4E" } })],
        [role == "tool", el(".conversation-role", Icon("formula"), "Tool", { style: { color: "#A6A22B" } })],
        [role == "reasoning", el(".conversation-role", Icon("brain-2-line"), "Reasoning", { style: { color: "#CE40AF" } })],
        [role == "hidden", el(".conversation-role", Icon("eye-off-line"), "Verbose", { style: { color: "#536a80ff" } })],
    )
}

function tryReformatJson(json) {
    try {
        const parsed = JSON.parse(json);
        return JSON.stringify(parsed, null, 4);
    } catch {
        return json;
    }
}

function getUsageTokenCount(usage, key, approximatedKey) {
    if (!usage)
        return null;

    const value = Number(usage[key]);
    if (Number.isFinite(value) && value > 0)
        return value;

    const approximatedValue = Number(usage[approximatedKey]);
    return Number.isFinite(approximatedValue) && approximatedValue > 0
        ? approximatedValue
        : null;
}

function formatUsageTokenCount(value) {
    if (!Number.isFinite(value))
        return "";

    const absoluteValue = Math.abs(value);
    if (absoluteValue < 1000)
        return `${formatNumber(absoluteValue)} tokens`;

    const compactValue = absoluteValue < 1000000
        ? [absoluteValue / 1000, "K"]
        : [absoluteValue / 1000000, "M"];

    const number = compactValue[0] >= 10
        ? Math.round(compactValue[0]).toString()
        : compactValue[0].toFixed(1).replace(/\.0$/, "");

    return `${number}${compactValue[1]}`;
}

function formatGeneratedDelta(milliseconds) {
    if (!Number.isFinite(milliseconds) || milliseconds < 0)
        return null;

    return `${(milliseconds / 1000).toFixed(2).replace(/\.?0+$/, "").replace(".", ",")}s`;
}

function ConversationGeneratedAt(generatedAt, previousGeneratedAt) {
    if (!generatedAt || !previousGeneratedAt)
        return null;

    const timestamp = dayjs(generatedAt);
    const previousTimestamp = dayjs(previousGeneratedAt);
    if (!timestamp.isValid() || !previousTimestamp.isValid())
        return null;

    const delta = formatGeneratedDelta(timestamp.diff(previousTimestamp));
    if (!delta)
        return null;

    return el(".conversation-message-generated-at",
        Icon("time-line"),
        el("time",
            delta,
            {
                datetime: generatedAt,
                title: `${previousTimestamp.format("DD/MM/YYYY HH:mm:ss.SSS")} -> ${timestamp.format("DD/MM/YYYY HH:mm:ss.SSS")}`
            }
        )
    );
}

export function getConversationMessageWeight(message) {
    if (message.role == "hidden" || message.role == "reasoning")
        return 0;

    const contentLength = typeof message.content === "string"
        ? message.content.length
        : JSON.stringify(message.content ?? "").length;
    const toolCallsLength = JSON.stringify(message.tool_calls ?? "").length;
    const metadataLength = (message.name?.length ?? 0) + (message.tool_call_id?.length ?? 0);

    return Math.ceil((contentLength + toolCallsLength + metadataLength) / 4);
}

export function getConversationUsageWeight(usage) {
    const cachedInputTokens =
        getUsageTokenCount(usage?.prompt_token_details, "cached_tokens") ??
        getUsageTokenCount(usage?.prompt_tokens_details, "cached_tokens");
    const inputTokens = getUsageTokenCount(usage, "prompt_tokens");
    const outputTokens = getUsageTokenCount(usage, "completion_tokens", "approximated_completion_tokens");

    return (cachedInputTokens ?? 0) + (inputTokens ?? 0) + (outputTokens ?? 0);
}

function ConversationUsageIndicator(usage, previousUsage) {
    const cachedInputTokens =
        getUsageTokenCount(usage?.prompt_token_details, "cached_tokens") ??
        getUsageTokenCount(usage?.prompt_tokens_details, "cached_tokens");
    const inputTokens = getUsageTokenCount(usage, "prompt_tokens");
    const outputTokens = getUsageTokenCount(usage, "completion_tokens", "approximated_completion_tokens");

    if (cachedInputTokens == null && inputTokens == null && outputTokens == null)
        return null;

    const previousCachedInputTokens =
        getUsageTokenCount(previousUsage?.prompt_token_details, "cached_tokens") ??
        getUsageTokenCount(previousUsage?.prompt_tokens_details, "cached_tokens");
    const previousInputTokens = getUsageTokenCount(previousUsage, "prompt_tokens");
    const previousOutputTokens = getUsageTokenCount(previousUsage, "completion_tokens", "approximated_completion_tokens");

    function usageItem(type, icon, value, previousValue) {
        if (value == null)
            return null;

        const delta = previousValue == null ? null : value - previousValue;

        return el(".conversation-usage-item",
            {
                dataUsageValue: type
            },
            Icon(icon),
            el("span", formatUsageTokenCount(value), { title: `${formatNumber(value)} tokens` }),
            delta && el("span.conversation-usage-delta",
                `${delta > 0 ? "+" : "-"}${formatUsageTokenCount(delta)}`
            ) || ""
        );
    }

    return el(".conversation-usage-indicator",
        usageItem("cached-in", "arrow-down-box-line", cachedInputTokens, previousCachedInputTokens),
        usageItem("in", "arrow-up-line", inputTokens, previousInputTokens),
        usageItem("out", "arrow-down-line", outputTokens, previousOutputTokens)
    );
}

export function ConversationMessage({ role, content, tool_calls, name, tool_call_id, usage, previousUsage, usageHeat, heatMapEnabled, generated_at, previousGeneratedAt }) {
    const showMessageMetrics = role == "user" || role == "assistant";

    function messageContent({ type, ...data }) {

        const renderingMarkdown = el.state.create(false);
        const renderingJson = el.state.create(false);

        if (type == "text") {

            const trimmedText = typeof data.text === "string" ? data.text.trim() : "";
            const isJsonDelimited = trimmedText.length > 1
                && ((trimmedText.startsWith("{") && trimmedText.endsWith("}"))
                    || (trimmedText.startsWith("[") && trimmedText.endsWith("]")));

            if (!trimmedText.length)
                return null;

            let parsedJson = null;
            let hasValidJson = false;

            if (data.text?.startsWith && data.text.startsWith('!#[') && data.text.trim()) {
                return el(".message-content",
                    BlockCodeWrapper("Message part", data.text, "text")
                );
            } else {

                if (isJsonDelimited) {
                    try {
                        parsedJson = JSON.parse(trimmedText);
                        hasValidJson = true;
                        renderingJson.value = true;
                        renderingMarkdown.value = false;
                    } catch {
                        hasValidJson = false;
                        renderingMarkdown.value = role === "assistant";
                    }
                }

                return el.fragment(
                    el(".message-content",
                        el(".message-toolbar",
                            ButtonRow(
                                !hasValidJson && ToggleButton(Icon("markdown-line"), "Render markdown", {
                                    value: renderingMarkdown.value,
                                    onToggle(state) {
                                        renderingMarkdown.value = state.value;
                                        if (state.value) {
                                            renderingJson.value = false;
                                        }
                                    }
                                }),
                                hasValidJson && ToggleButton(Icon("braces-line"), "Render JSON", {
                                    value: renderingJson.value,
                                    onToggle(state) {
                                        renderingJson.value = state.value;
                                        if (state.value) {
                                            renderingMarkdown.value = false;
                                        }
                                    }
                                }),
                                Button(Icon("file-copy-line"), "Copy", {
                                    onClick() {
                                        copyToClipboard(data.text);
                                    }
                                })
                            )
                        ),
                        el(".message-text.markdown-content.wrapped-markdown-content",
                            el.state.listening([renderingMarkdown, renderingJson], () => el("div",
                                cond(
                                    [renderingJson.value, el.raw(renderMarkdown("```json\n" + JSON.stringify(parsedJson, null, 4) + "\n```"))],
                                    [renderingMarkdown.value, el.raw(renderMarkdown(data.text))],
                                    [true, el(".wrapped-raw", data.text)]
                                )
                            ))
                        ),
                        el(".message-footer",
                            MutedText(
                                "~", formatNumber(data.text.length / 4), " tokens"
                            )
                        )
                    )
                )
            }

        } else if (type == "image_url") {
            return el("img.message-content",
                {
                    src: data.image_url.url,
                    onClick(event) {
                        displayAlert({
                            title: "Chat image",
                            dialogSize: "lightbox",
                            message: [
                                el("img", {
                                    src: event.target.src
                                })
                            ]
                        });
                    }
                }
            )
        } else if (type == "video_url") {
            return el("video.message-content[controls]", { src: data.video_url.url });
        } else if (type == "input_audio") {
            return el("audio.message-content[controls]",
                { src: "data:audio/" + data.input_audio.format + ";base64," + data.input_audio.data }
            );
        } else if (type == "file") {
            const mimeType = data.file?.file_data?.split(";")[0].replace("data:", "") ?? "";
            if (mimeType.startsWith("video/")) {
                return el("video.message-content[controls]", { src: data.file.file_data });
            } else if (mimeType.startsWith("audio/")) {
                return el("audio.message-content[controls]", { src: data.file.file_data });
            } else if (mimeType.startsWith("image/")) {
                return el("img.message-content", { src: data.file.file_data });
            } else if (mimeType === "application/pdf") {
                const base64 = data.file.file_data.split(",")[1];
                const bytes = Uint8Array.from(atob(base64), c => c.charCodeAt(0));
                const blobUrl = URL.createObjectURL(new Blob([bytes], { type: "application/pdf" }));
                return el("iframe.message-content.pdf-preview",
                    { src: blobUrl }
                );
            } else {
                return el(".message-content.file-attachment",
                    Icon("attachment-2-line"),
                    el("span", data.file?.filename ?? "Arquivo")
                );
            }
        } else if (type == "document") {
            const source = data.source ?? data.document;
            if (source?.type === "base64" && source.media_type?.startsWith("image/")) {
                return el("img.message-content", { src: "data:" + source.media_type + ";base64," + source.data });
            }
            const label = data.title ?? (source?.type === "url" ? source.url : "Documento");
            return el(".message-content.file-attachment",
                Icon("file-text-line"),
                el("span", label)
            );
        }
    }

    function toolCalls(tool_calls) {
        return el(".message-content.tool-calls-block",
            tool_calls.map(tc => BlockCodeWrapper(
                el("span", tc.function.name, MutedText(" - tool call ID. ", tc.id)),
                tryReformatJson(tc.function.arguments),
                "json"
            ))
        )
    }

    return el("details.conversation-message",
        usageHeat != null && {
            dataUsageHeat: true,
            style: `--conversation-usage-heat: ${usageHeat.toFixed(3)}; --conversation-usage-heat-percent: ${Math.round(usageHeat * 100)}%; --conversation-usage-heat-alpha: ${Math.round(6 + usageHeat * 60)}%;`
        },
        {
            dataHeatEnabled: heatMapEnabled
        },
        el("summary.conversation-message-title",
            ConversationRole(role),
            name && InlineCode(el.fragment(MutedText("for tool ", { style: { fontStyle: "italic" } }), name, MutedText(", ID. ", tool_call_id))),
            showMessageMetrics && ConversationUsageIndicator(usage, previousUsage),
            (role == "assistant" || role == "judge") && ConversationGeneratedAt(generated_at, previousGeneratedAt)
        ),
        el("div.conversation-contents",
            content && cond(
                [Array.isArray(content), () => content.map(messageContent)],
                [role == "tool", () => BlockCodeWrapper("Tool result", content, "text")],
                [role == "hidden", () => BlockCodeWrapper("Verbose message", content, "text")],
                [true, () => messageContent({ type: 'text', text: content })]
            ),
            tool_calls && toolCalls(tool_calls),
            {
                mount() {
                    Prism.highlightAll();
                }
            }
        )
    )
}

export function ConversationInfoMessage({ variant, icon, label, content }) {
    return el("details.conversation-message.conversation-message-info",
        { 'data-variant': variant, open: !!content },
        el("summary.conversation-message-title",
            el(".conversation-role", Icon(icon), label)
        ),
        content && el("div.conversation-contents",
            el(".message-content",
                el(".message-text.wrapped-raw", content)
            )
        )
    );
}
